import {createApp, h} from "vue";
import {mask} from "vue-the-mask"
import {createInertiaApp, Link} from "@inertiajs/vue3";
import {autoAnimatePlugin} from "@formkit/auto-animate/vue";
import Toast, {POSITION} from "@brackets/vue-toastification";
import "@brackets/vue-toastification/dist/index.css";
import {ZiggyVue} from "ziggy/src/js";
import {AuthenticatedLayout, GuestLayout} from "../Layouts/index";
import {Notification} from "craftable-pro/Components";
import {
    i18nVue,
    loadTranslations,
} from "craftable-pro/plugins/laravel-vue-i18n";
import {can} from "craftable-pro/plugins/can";
import {PageProps} from "craftable-pro/types/page";
import LandingPageLayout from "@/Layouts/LandingPageLayout.vue";
import VueApexCharts from 'vue3-apexcharts'
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import {MotionPlugin} from '@vueuse/motion'
import * as Sentry from "@sentry/vue";


const appName = "Recoloca.ai";

const lang = document.documentElement.lang
    ? document.documentElement.lang.replace("-", "_")
    : "en";

createInertiaApp({
    title: (title) => {
        const titleElement = document.querySelector("title");

        if (titleElement && !titleElement.hasAttribute("inertia")) {
            titleElement.remove();
        }

        return title ? `${title} - ${appName}` : appName;
    },
    progress: {color: "#4B5563"},
    resolve: async (name: string) => {
        // Getting all the pages from the craftable-pro package
        const craftableProPagesGlob = import.meta.glob(
            "../../../vendor/brackets/**/resources/js/Pages/**/*.vue",
        );

        // Getting all local pages
        const pagesLocalGlob = import.meta.glob("./Pages/**/*.vue");

        // Resolving all the pages from the craftable-pro package and the local package
        const [pagesCraftablePro, pagesLocal] = await Promise.all([
            craftableProPagesGlob,
            pagesLocalGlob,
        ]);

        // Merging all the pages from the craftable-pro package and the local package with the local ones taking precedence
        const pages = {...pagesLocal, ...pagesCraftablePro};

        // Finding the page that matches the name
        const pagePath = Object.keys(pages).find((key) =>
            key.endsWith(`/${name}.vue`),
        );

        // Throwing an error if the page is not found
        if (!pagePath) {
            throw new Error(`Page '${name}' not found.`);
        }

        // Getting the page component
        const pageComponent = (await pages[pagePath]()).default;

        const page = pageComponent;

        // Setting the layout if it's not set
        if (page.layout === undefined) {
            if (name.startsWith("Auth/")) {
                page.layout = GuestLayout;
            } else if (name.startsWith("Guest/") || name.includes('LandingPage') || name.includes('Terms') || name.includes ('CookiesPolicy')){
                page.layout = LandingPageLayout;
            } else {
                page.layout = AuthenticatedLayout;
            }
        }

        return page;
    },

    setup({el, App, props, plugin}) {

        loadTranslations(
            `/lang/${
                (props.initialPage.props.auth as PageProps["auth"])?.user?.locale ??
                lang
            }/craftable-pro.json`,
            (translations: JSON) => {
                const app = createApp({render: () => h(App, props)});

                const isProduction = import.meta.env.VITE_APP_ENV === "production";

                if (isProduction && import.meta.env.VITE_SENTRY_DSN_PUBLIC) {
                    Sentry.init({
                        app,
                        dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
                        release: import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_COMMIT_SHA,
                        integrations: [
                            Sentry.browserTracingIntegration(),
                            Sentry.replayIntegration(),

                            Sentry.feedbackIntegration({
                                // Opções Gerais
                                autoInject: true, // Mantém o botão padrão, você pode mudar para `false` se quiser usar seu próprio botão
                                showBranding: false, // Opcional: remove a logo "Powered by Sentry"
                                colorScheme: 'light', // Pode ser 'light', 'dark', ou 'system'

                                // Textos em Português
                                triggerLabel: "Enviar feedback",
                                triggerAriaLabel: "Abrir formulário de feedback",
                                formTitle: "Compartilhe sua experiência",
                                submitButtonLabel: "Enviar relato",
                                cancelButtonLabel: "Fechar",
                                nameLabel: "Nome completo",
                                namePlaceholder: "Digite seu nome",
                                emailLabel: "E-mail de contato",
                                emailPlaceholder: "exemplo@seuemail.com",
                                messageLabel: "Descrição do ocorrido",
                                messagePlaceholder: "Conte-nos o que aconteceu e como podemos melhorar",
                                successMessageText: "Obrigado pelo seu relato! Nossa equipe vai analisá-lo com atenção e, se necessário, entraremos em contato.",
                                isRequiredLabel: "(obrigatório)",
                                addScreenshotButtonLabel: "Anexar captura de tela",
                                removeScreenshotButtonLabel: "Remover captura",
                                highlightToolText: "Destacar área",
                                hideToolText: "Ocultar informações",
                                removeHighlightText: "Remover destaque",
                                screenshotTitle: "Captura adicionada",
                                screenshotSubtitle: "Uma imagem da sua tela foi anexada ao seu relato para facilitar nossa análise.",
                                screenshotRemoveLabel: "Remover captura",
                            }),
                        ],
                        tracesSampleRate: 1.0,
                        tracePropagationTargets: ["localhost", /^https:\/\/recoloca\.ai/],
                        replaysSessionSampleRate: 0.1, // Sample 10% of sessions
                        replaysOnErrorSampleRate: 1.0, // If an error happens, sample 100% of sessions with errors
                    });

                    const user = (props.initialPage.props.auth as PageProps["auth"])?.user;
                    if (user) {
                        console.log('Setting Sentry user:', user);
                      Sentry.setUser({
                        id: user.id,
                        email: user.email,
                        fullName: user.first_name + ' ' + user.last_name,
                        username: user.first_name + ' ' + user.last_name,
                          name: user.first_name + ' ' + user.last_name,
                      });
                    }
                }


                app.use(plugin)
                    .use(Toast, {
                        transition: "Vue-Toastification__fade",
                        rootComponent: Notification,
                        position: POSITION.BOTTOM_RIGHT,
                    })
                    .use(i18nVue, {
                        resolve: (lang: string) => {
                            return translations;
                        },
                    })
                    .use(autoAnimatePlugin)
                    .use(ZiggyVue)
                    .use(VueApexCharts)
                    .use(FloatingVue)
                    .use(MotionPlugin)
                    .component("Link", Link)
                    .directive("can", can)
                    .directive("mask", mask)
                    .mount(el);
            }
        );
    },
});
